import { useMutation, useQuery, useQueryClient } from 'react-query';
import { chamarOktonBank, enviarOktonBank } from '../oktonbank';

// Uma linha do Plano de Contas da DFC. conta_pct nula marca o Nível, que só
// agrupa; preenchida marca a Conta, que recebe a classificação.
// item_pct é a numeração hierárquica (1.1.2) e parent_pct aponta para o item do
// Nível acima — a raiz aponta para si mesma e termina em ponto ('1.').
export interface PlanoDFC {
  id_pct: string;
  item_pct: string;
  parent_pct: string | null;
  nome_pct: string;
  conta_pct: string | null;
}

export interface DadosPlanoDFC {
  tipo: 'nivel' | 'conta';
  item: string;
  nome: string;
  conta: string;
}

export function usePlanoDFC() {
  return useQuery(
    ['oktonbank-planodfc'],
    async () => {
      const dados = await chamarOktonBank<{ rows: PlanoDFC[] }>(
        'OBCadastros',
        'listarPlanoDFC',
      );
      return Array.isArray(dados?.rows) ? dados.rows : [];
    },
    { staleTime: 1000 * 60 * 5 },
  );
}

// Mexer na árvore muda o que os seletores de Plano de Contas oferecem, então as
// listas deles envelhecem junto.
function invalidarPlano(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries('oktonbank-planodfc');
  queryClient.invalidateQueries('oktonbank-planosdfc');
  queryClient.invalidateQueries('oktonbank-planosdre');
}

export function useSavePlanoDFC() {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, ...payload }: DadosPlanoDFC & { id?: string }) =>
      enviarOktonBank('OBCadastros', 'gravarPlanoDFC', {
        id: id ?? '',
        ...payload,
      }),
    { onSuccess: () => invalidarPlano(queryClient) },
  );
}

// Próximo item e próxima Conta livres dentro de um Nível, como a tela do
// Plano de Contas do IFLEX sugere ao adicionar. Só busca com o Nível em mãos.
export function useSugestaoPlanoDFC(nivel: string) {
  return useQuery(
    ['oktonbank-planodfc-sugestao', nivel],
    () =>
      chamarOktonBank<{ item: string; conta: number }>(
        'OBCadastros',
        'sugerirPlanoDFC',
        { nivel },
      ),
    { enabled: nivel !== '' },
  );
}

export function useDeletePlanoDFC() {
  const queryClient = useQueryClient();

  return useMutation(
    (id: string) => enviarOktonBank('OBCadastros', 'removerPlanoDFC', { id }),
    { onSuccess: () => invalidarPlano(queryClient) },
  );
}

// Contas que o IFLEX reserva e não deixa mexer.
const CONTAS_RESERVADAS = ['2911', '1101', '2101'];

/**
 * Por que este registro não pode ser editado nem excluído, ou vazio quando
 * pode. Mesma regra da tela do IFLEX, que desabilita os dois botões.
 *
 * Os dois primeiros níveis são a espinha da DFC: mexer neles reescreve a
 * classificação de tudo que está abaixo. O nível vem da profundidade na
 * árvore, contada a partir de 1 na raiz, como o nivel_pct de lá.
 */
export function bloqueioPlanoDFC(no: NoPlanoDFC): string {
  if (no.profundidade + 1 < 3) {
    return 'Os dois primeiros níveis da DFC não podem ser alterados nem excluídos.';
  }
  if (no.conta_pct && CONTAS_RESERVADAS.includes(no.conta_pct)) {
    return 'Esta Conta é reservada pelo IFLEX.';
  }
  return '';
}

export interface NoPlanoDFC extends PlanoDFC {
  profundidade: number;
  filhos: NoPlanoDFC[];
}

/**
 * Monta a árvore a partir da lista achatada.
 *
 * O vínculo é por item: parent_pct guarda o item_pct do Nível acima. A raiz
 * aponta para si mesma, então ela é reconhecida por isso — e é o que impede a
 * montagem de entrar em laço.
 */
export function montarArvore(linhas: PlanoDFC[]): NoPlanoDFC[] {
  // Primeiro quem é filho de quem, só com os itens; a árvore em si é montada
  // depois, de cima para baixo, já com a profundidade certa.
  const filhosDe = new Map<string, PlanoDFC[]>();
  const raizes: PlanoDFC[] = [];
  linhas.forEach(linha => {
    const pai = linha.parent_pct;
    // A raiz aponta para si mesma; sem este corte a montagem entraria em laço.
    if (!pai || pai === linha.item_pct) {
      raizes.push(linha);
      return;
    }
    filhosDe.set(pai, [...(filhosDe.get(pai) ?? []), linha]);
  });

  // Um item só é montado uma vez. Dois itens que apontam um para o outro —
  // parent_pct é texto livre, nada no banco impede — não teriam raiz e sumiriam
  // da tela, ou fariam a montagem recursar sem fim.
  const montados = new Set<string>();

  // A profundidade sai da árvore montada, e não da contagem de pontos: item
  // fora do padrão continua aparecendo no lugar certo.
  const montar = (linha: PlanoDFC, profundidade: number): NoPlanoDFC => {
    montados.add(linha.item_pct);
    return {
      ...linha,
      profundidade,
      filhos: (filhosDe.get(linha.item_pct) ?? [])
        .filter(filho => !montados.has(filho.item_pct))
        .map(filho => montar(filho, profundidade + 1)),
    };
  };

  const arvore = raizes.map(linha => montar(linha, 0));
  // O que não pendurou em raiz nenhuma sobe para o topo: pai apagado por fora,
  // ou dois itens apontando um para o outro. Sumir da tela seria pior — o
  // registro existe e precisa poder ser corrigido.
  linhas
    .filter(linha => !montados.has(linha.item_pct))
    .forEach(linha => arvore.push(montar(linha, 0)));
  return arvore;
}
